import { useEffect, useMemo, useRef } from "react";
import { useGLTF, useAnimations } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { RigidBody } from "@react-three/rapier";
import * as THREE from "three";
import { SkeletonUtils } from "three-stdlib";
import { addEventListener } from "../src/events/eventBus";

export default function DanceFloor({
  position = [4.71, -0.07, 33.51],
  rotation = [0, 0, 0],
  scale = 1,
  riseOffset = 4.5,
  riseDuration = 1,
  ...rigidBodyProps
}) {
  const group = useRef();
  const rigidBodyRef = useRef(null);
  const isRisingRef = useRef(false);
  const hasRaisedRef = useRef(false);
  const currentYRef = useRef(position[1] - riseOffset);
  const targetPositionRef = useRef({ x: position[0], y: position[1], z: position[2] });
  const { scene, animations } = useGLTF("/dancefloor.glb");
  const danceFloorScene = useMemo(() => SkeletonUtils.clone(scene), [scene]);
  const { actions } = useAnimations(animations, group);
  const hiddenY = useMemo(() => position[1] - riseOffset, [position, riseOffset]);
  const riseSpeed = useMemo(() => {
    if (riseDuration <= 0) return Infinity;
    return Math.abs(riseOffset) / riseDuration;
  }, [riseOffset, riseDuration]);

  useEffect(() => {
    danceFloorScene.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
  }, [danceFloorScene]);

  useEffect(() => {
    if (!rigidBodyRef.current) return undefined;

    const body = rigidBodyRef.current;
    body.userData = body.userData || {};
    body.userData.isDanceFloor = true;

    return () => {
      if (body.userData) {
        delete body.userData.isDanceFloor;
      }
    };
  }, []);

  useEffect(() => {
    targetPositionRef.current = { x: position[0], y: position[1], z: position[2] };
    if (!hasRaisedRef.current) {
      currentYRef.current = hiddenY;
      if (rigidBodyRef.current) {
        rigidBodyRef.current.setTranslation(
          { x: position[0], y: hiddenY, z: position[2] },
          true
        );
      }
    }
  }, [hiddenY, position]);

  useEffect(() => {
    if (!actions) return undefined;

    const clips = Object.values(actions);
    if (!clips.length) return undefined;

    clips.forEach((clip) => {
      clip.reset().setLoop(THREE.LoopRepeat, Infinity).play();
      clip.clampWhenFinished = false;
    });

    return () => {
      clips.forEach((clip) => {
        clip.stop();
      });
    };
  }, [actions]);

  useEffect(() => {
    const cleanup = addEventListener("cineModeStart", () => {
      if (hasRaisedRef.current) return;
      isRisingRef.current = true;
    });

    return cleanup;
  }, []);

  useFrame((_, delta) => {
    if (!isRisingRef.current || !rigidBodyRef.current) return;

    const target = targetPositionRef.current;
    const currentY = currentYRef.current;
    const speed = riseSpeed === Infinity ? Math.abs(target.y - currentY) / Math.max(delta, 1e-3) : riseSpeed;
    const nextY = Math.min(target.y, currentY + speed * delta);

    currentYRef.current = nextY;
    rigidBodyRef.current.setTranslation({ x: target.x, y: nextY, z: target.z }, true);

    if (Math.abs(nextY - target.y) <= 1e-3) {
      currentYRef.current = target.y;
      rigidBodyRef.current.setTranslation(target, true);
      isRisingRef.current = false;
      hasRaisedRef.current = true;
    }
  });

  return (
    <RigidBody
      ref={rigidBodyRef}
      type="fixed"
      colliders="trimesh"
      position={[position[0], hiddenY, position[2]]}
      rotation={rotation}
      {...rigidBodyProps}
    >
      <group ref={group} dispose={null} scale={scale}>
        <primitive object={danceFloorScene} />
      </group>
    </RigidBody>
  );
}

useGLTF.preload("/dancefloor.glb");
